/**
 * Copyright (c) 2018-present, Didi, Inc.
 * All rights reserved.
 *
 * @author Cory(kuanghongrui@didichuxing.com)
 *
 * @file The class of AOMGH5JsErrorPlugin
 */

import { IContext } from '@omega-tracker/core/src/com/didichuxing/context/IContext';
import { ITrackerConfiguration } from 'com/didichuxing/tracker/config/ITrackerConfiguration';
import { ITrackerContext } from 'com/didichuxing/tracker/context/ITrackerContext';
import { ITrackedEvent } from 'com/didichuxing/tracker/event/ITrackedEvent';
import { ITrackedEventArgs } from 'com/didichuxing/tracker/plugin/IReportingStrategyPlugin';
import AReportingStrategyPlugin from 'com/didichuxing/tracker/plugin/AReportingStrategyPlugin';
import OMGH5JsErrorEvent from 'com/didichuxing/tracker/h5/plugins/events/OMGH5JsErrorEvent';
import PluginUtils from 'com/didichuxing/tracker/plugin/utils/PluginUtils';
import UUID from 'com/didichuxing/tracker/plugin/utils/UUID';
import Md5 from 'com/didichuxing/tracker/plugin/utils/MD5';

export default abstract class AOMGH5JsErrorPlugin extends AReportingStrategyPlugin {
    /**
     * 错误类型
     */
    private typeList: string[] = ['global code', 'TypeError',
        'SyntaxError', 'ReferenceError', 'RangeError', 'URIError', 'EvalError'];

    /**
     * @override
     * @inheritDoc
     * @returns {string}
     */
    public getName(): string {
        return PluginUtils.PLUGIN_NAME_JS_ERROR;
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public install(): boolean {
        this.handleError = this.handleError.bind(this);
        window.addEventListener('error', this.handleError);
        // window.addEventListener('unhandledrejection', this.handleError);
        this.reportbeforeError();
        return super.install();
    }

    /**
     * 发送onloan之前的错误
     */
    public reportbeforeError(): void {
        const beforeErrorList = this.getTracker().beforeErrorList || [];
        beforeErrorList.forEach((event) => {
            this.handleError(event, 'beforeLoad');
        });
        this.getTracker().beforeErrorList = [];
    }

    /**
     * @override
     * @inheritDoc
     * @returns {IContext}
     */
    protected get initialzedContext(): IContext {
        const context: ITrackerContext = this.getContext() as ITrackerContext;
        return {
            ...super.initialzedContext,
            autoError: typeof context.autoError === 'boolean' ? context.autoError : true
        } as IContext;
    }

    /**
     * @override
     * @inheritDoc
     */
    public execute(): void {
        /* Nothing to execute */
    }

    /**
     * @override
     * @inheritDoc
     */
    public exit(): void {
        /* Nothing to exit */
    }

    /**
     * 获取Breadcrumb
     */
    private getBreadcrumbList(): string[] {
        const breadcrumbListName = (window as any)[this.breadcrumbListName] || [];
        if (breadcrumbListName.length > 10) {
            (window as any)[this.breadcrumbListName].length = 10;
        }
        return breadcrumbListName;
    }

    /**
     * 获取错误类型
     * @param message
     */
    private getErrorType(message: string): string {
        let result: string = message || 'Empty';
        this.typeList.forEach((type: string): void => {
            if (message.indexOf(type) !== -1) {
                result = type;
            }
        });
        return result;

    }

    /**
     * handle js error.
     */
    protected handleError(event: ErrorEvent, occasion: string = 'afterLoad'): void {
        // 过滤ResizeObserver 错误的上报。在 Chrome 83 无限上报会造成浏览器卡死
        if (event.message && event.message.indexOf('ResizeObserver') !== -1) {
          return;
        }
        const type = this.getErrorType(event.message);
        this.report({
            eventId: PluginUtils.EVENT_ID_JS_ERROR,
            attrs: {
                label: event.error ? event.error.stack : event.message,
                line: event.lineno,
                url: event.filename,
                col: event.colno,
                message: event.message,
                stack: event.error ? (event.error.stack || '') : '',
                breadcrumb: this.getBreadcrumbList(),
                UUID: UUID.v4,
                type: type,
                typeMd5: Md5.hashStr(type),
                occasion: occasion
            }
        });
    }

    /**
     * @override
     * @inheritDoc
     * @param {ITrackedEventArgs} eventArgs
     * @returns {ITrackedEvent}
     */
    protected generateEvent(eventArgs: ITrackedEventArgs): ITrackedEvent {
        return new OMGH5JsErrorEvent(eventArgs.attrs, this.getContext());
    }

    /**
     * @override
     * @inheritDoc
     * @returns {boolean}
     */
    public uninstall(): boolean {
        window.removeEventListener('error', this.handleError);
        // window.removeEventListener('unhandledrejection', this.handleError);
        return super.uninstall();
    }
}
